import arrayToTree from 'array-to-tree';
import { get, uniq, size, orderBy, flatten } from 'global/lib/lodash';
import { waitIdle } from 'global/utils';
import { AUTOTAG } from './index';

export const getId = (item) => {
  if (!item) {
    return -1;
  }
  return item._id || item.id;
};

export const getBuiltId = (item) => {
  if (!item) {
    return -1;
  }

  return ['assessmentId', 'checklistId', 'checkpointId', 'scorecardId', 'supersetId'].map((s) => `${get(item, s)}`).join('_');
};

export const getItemType = (item) => {
  let type = '';
  if (!item) {
    return 'none';
  }

  const isGobs = isGob(item);

  if (item.type) {
    type = item.type;
  } else if (isGobs) {
    type = 'checkpoint';
  } else if (item.checkpointId) {
    type = 'checkpoint';
  } else if (item.checklistId) {
    type = 'checklist';
  } else if (item.systemId) {
    type = 'system';
  } else if (item.supersetId) {
    type = 'superset';
  } else if (item.selectAll !== undefined) {
    type = 'selectAll';
  }

  return type;
};

export const getItemKey = (item) => {
  return `${getItemType(item)}_${getId(item)}`;
};

export const isGob = (item) => {
  return item.depth === 2 && !!item.scorecardScoreId ? true : false;
};

const taskMatchesItem = (item) => (task) => {
  // subscores in location view: match by subScore.id
  if (item.isSubScore && get(task, 'subScore.id') === get(item, 'id')) return true;
  // subscore tasks in category view: match by checkpointId against parent checkpoint
  if (!item.isSubScore && get(task, 'subScore.id') && task.checkpointId === get(item, 'checkpointId')) return true;
  // General observations and regular checkpoints
  return (!!task.scorecardScoreId && task?.scorecardScoreId === item?.scorecardScoreId || (task?.scorecardScore?.lineageHash && task.scorecardScore.lineageHash === item.lineageHash));
}

export const findTaskForItem = (item, tasks) => {
  if (!item) return null;
  return tasks.find(taskMatchesItem(item))?? null;
};

export const findTasksForItem = (item, tasks) => {
  if (!item) return [];
  return tasks.filter(taskMatchesItem(item));
};

export const filterData = async (d, filter, preserveOrder = true) => {
  await waitIdle();
  const { value, hideNa, search, listGroup, onlyLoc, assessment, roomcps, useLocation, activeSc, suggestedCpIds, assessmentTasks, isDefault } = filter;
  const locationValuesOnly = listGroup === 'location' || onlyLoc;
  const isCatLoc = listGroup === 'category' && onlyLoc;
  const currentFilt = value;
  const searchLower = search ? search.toLowerCase().trim() : '';
  let hasChildren = [];
  let subscores = [];

  if (useLocation && activeSc) {
    d = get(filter, 'filterHook.filterAssessmentItems', () => d)(d, activeSc.edition, { assessment });
  }

  if (roomcps) {
    const { id, cps } = roomcps;

    d = d.filter((item, i) => {
      const type = getItemType(item);

      if (type === 'checkpoint' && item.checkpointId && !cps.includes(item.checkpointId.toString())) {
        // even if a checkpoint is not in the current room's list,
        // if it contains an SubScoreModal assigned to the current room, include it
        return get(item, 'subscores', []).reduce((agg, i) => agg || get(i, 'realm.id') === id, false);
      }

      return true;
    });
  }

  if (currentFilt !== undefined && (currentFilt !== 'any' || locationValuesOnly || hideNa || search || useLocation)) {
    await waitIdle();

    const itemPassesFilters = (item) => {
      if ((hideNa || listGroup === 'location') && item.displayValue === 'N/A') {
        return false;
      } else if (item.depth === 3 || isGob(item)) {
        // if it's a scored item
        const passesSearch = (() => {
          if (!search) return true;
          const crit = ((item.name || '') + (item.criteria || '') + (item.checklist?.name || '')).toLowerCase();
          if (crit.indexOf(searchLower) !== -1) return true
          if (!isGob(item) && suggestedCpIds && suggestedCpIds.length) {
            return suggestedCpIds.includes(item.checkpointId?.toString());
          }
          return false
        })();
        if (isGob(item) && (currentFilt !== 'hasTask')) { return passesSearch };
        if (currentFilt !== 'any') {
          // search with button filters
          const defFilts = ['high', 'low', 'act'];
          if (defFilts.indexOf(currentFilt) > -1) {
            return get(item, `filters.${currentFilt}`) && passesSearch;
          } else if (currentFilt === 'missedTarget') {
            const cscore = parseFloat(item.value);
            const targetscore = parseFloat(get(item, 'target.value'));
            if (isNaN(targetscore)) {
              return false;
            } else if (isNaN(cscore)) {
              return passesSearch;
            }

            return cscore < targetscore && passesSearch;
          } else if (currentFilt === 'hasTarget') {
            const targetscore = parseFloat(get(item, 'target.value'));
            if (isNaN(targetscore)) {
              return false;
            }
            return passesSearch;
          } else if (currentFilt === 'hasTask') {
            const tasks = assessmentTasks || [];
            const hasMatch = !!findTaskForItem(item, tasks);
            return hasMatch && passesSearch;
          } else {
            //filter is using the select dropdown
            const { value, calculatedScore } = item;
            const score = currentFilt === -1 ? value : calculatedScore; //for some reason neutral comes over as a calculated score as 1
            if (score === null) {
              return currentFilt === 'na' && passesSearch;
            } else {
              return parseInt(score) === currentFilt && passesSearch;
            }
          }
        } else {
          return passesSearch;
        }
      }
      return true;
    };

    const items = d.filter((item, i) => {
      let passes = itemPassesFilters(item);
      if (isCatLoc && size(item.subscores)) {
        passes = !!size(item.subscores);
      }
      // else if (listGroup === 'category' && size(item.subscores)) {
      //   passes = !!size(item.subscores.filter(itemPassesFilters));
      // }
      if (item.subscores) {
        subscores = [...subscores, ...item.subscores.filter(itemPassesFilters)];
      }
      return passes;
    });

    await waitIdle();

    if (listGroup === 'location') {
      // const subscoreRealms = uniq((subscores || []).map((i) => get(i,'room')));
      const scorecardName = get(
        items.find((i) => !get(i, 'parentId')),
        'name',
        'Scorecard Name'
      );
      const assessmentName = get(assessment, 'data.realm.name', 'Location');

      const subScores = orderBy(subscores || [], 'createdAt', 'asc').filter((ss) => get(ss, 'notes') != AUTOTAG);
      const subscoreRealms = subScores.reduce((agg, i) => {
        const realm = i.realm;
        const foundPssvCheckpoint = d.reduce((returnVal, val, index) => {
          if (val.checklistId === i.checklistId && val.checkpointId === i.checkpointId) {
            return val;
          }
          return returnVal;
        }, {});

        const foundRealm = agg.findIndex((a) => a.id == get(i, 'realmId'));
        const subScore = { ...i, pssvParentCheckpoint: foundPssvCheckpoint };

        if (foundRealm !== -1) {
          get(agg, `${foundRealm}.children`).push(subScore);
        } else if (!!realm) {
          agg.push({ ...realm, children: [subScore] });
        }

        return agg;
      }, []);

      const scoreDict = {
        scorecardId: '',
        assessmentId: '',
      };
      hasChildren = [
        {
          ...scoreDict,
          name: scorecardName,
          displayValue: size(subScores),
          depth: 0,
          id: 'realm1',
          type: 'selectAll',
          children: [
            {
              ...scoreDict,
              name: assessmentName,
              depth: 0,
              id: '1',
              type: 'system',
              displayValue: size(subScores),
              children: subscoreRealms.map((room, i) => ({
                ...scoreDict,
                id: room.id,
                name: get(room, 'name', 'none'),
                displayValue: size(get(room, 'children')),
                type: 'room',
                depth: 1,
                order: i,
                parentId: 1,
                children: get(room, 'children', []).map((c) => ({ ...c, parentId: room.id })),
              })),
            },
          ],
        },
      ];
    } else {
      const filteredTree = arrayToTree(items, { parentProperty: 'parentId' });
      const orderBySearch = (i) => {
        if (preserveOrder || !search) {
          return 0;
        }
        const match = get(i, 'name').toLowerCase().indexOf(searchLower) === -1 ? 0 : 1;
        const cMatch = get(i, 'children', []).reduce((agg, a) => {
          const num = get(a, 'name', '').toLowerCase().indexOf(searchLower) === -1 ? 0 : 1;
          return agg + num;
        }, 0);
        return match + cMatch;
      };
      hasChildren = orderBy(filteredTree, ['order']).filter((a) => {
        a.children = orderBy(get(a, 'children', []), [orderBySearch, 'order', 'name'], ['desc', 'asc', 'asc']).filter((af) => {
          af.children = orderBy(get(af, 'children', []), [orderBySearch, 'order'], ['desc', 'asc']).filter((ac) => {
            const childs = get(ac, 'children', []);
            ac.children = orderBy(childs, ['name']).sort((a, b) => {
              if (!a.checkpointId || !b.checkpointId) {
                return !a.checkpointId ? -1 : 1;
              }
              return 0;
            });

            return isCatLoc
              ? !!size(
                  flatten(
                    ac.children.map((cc) => {
                      // if roomcps is set, we only return subscores from the current room
                      const subScores = roomcps ? cc.subscores.filter((i) => get(i, 'realm.id', '') === get(roomcps, 'id')) : cc.subscores;
                      return subScores || [];
                    })
                  )
                )
              : !!size(childs.filter((ac) => isDefault || !isGob(ac)));
              // Show all checklists if the filters are default,
              // if not, don't show checklists where the only checkpoint is a general observation
          });
          return !!size(af.children);
        });
        return !!size(a.children);
      });
    }
  } else {
    if (listGroup === 'location') {
      hasChildren = [];
    } else {
      if (roomcps) {
        // if roomcps is set, we only return subscores associated with the current room
        const dFilt = d.map((i) => {
          if (get(i, 'depth') !== 3) {
            return i;
          }
          const subScores = get(i, 'subScores', []).filter((ss) => get(ss, 'realm.id', '') === get(roomcps, 'id'));
          return { ...i, subscores: subScores };
        });
        hasChildren = arrayToTree(dFilt, { parentProperty: 'parentId' });
      } else {
        hasChildren = arrayToTree(d, { parentProperty: 'parentId' });
      }
    }
  }

  return {
    hasChildren,
  };
};

export const setFilters = (item) => {
  const value = parseInt(item.value);

  return {
    low: value !== -1 && value < 50,
    high: value > 50,
    act: isActionable(item),
  };
};

export const findAllActionableSubs = (item) => {
  if (item.subScores) {
    return flatten([...item.subScores]).filter((f) => !(f.notes === AUTOTAG || (f.score === 100 && !size(f.notes) && !size(f.images))));
  }
  return [];
};

export const isActionable = (item) => {
  if (item && item.value === null) {
    return false;
  }

  const value = parseInt(item.value);

  if (value === 100 || value === -1) {
    const hasImage = !!size(item.images);
    const hasNotes = !!size(item.notes);
    if (item.isSubScore === true) {
      if (get(item, 'notes') === AUTOTAG || (!hasImage && !hasNotes)) {
        return false;
      }
    } else {
      const isGobs = isGob(item);
      const subs = findAllActionableSubs(item);
      const hasSubs = !!size(subs);

      if (!hasImage && !hasNotes && !isGobs && !hasSubs) {
        return false;
      }
    }
  }

  return true;
};
